import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useIsMobile } from '@shared/hooks/useMobile';
import { CheckCircle2, Printer } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

enum ReturnedReceptionStatus {
  CLOSED = 0,
  OPEN = 1,
  POR_UBICAR = 2,
  PENDING_OPEN = 3,
}

interface BreadcrumbProps {
  receptionId: number;
  receptionCode?: string | null;
  receptionStatus?: number;
  onFinalizeClick?: () => void;
  onPrintClick?: () => void;
  isFinalizeDisabled?: boolean;
  finalizeTooltip?: string;
}

export const Breadcrumb = ({
  receptionId,
  receptionCode,
  receptionStatus,
  onFinalizeClick,
  onPrintClick,
  isFinalizeDisabled,
  finalizeTooltip,
}: BreadcrumbProps) => {
  const { t } = useTranslation('reusedModule');
  const isMobile = useIsMobile();

  const getStatusBadge = useCallback(
    (status?: number) => {
      switch (status) {
        case ReturnedReceptionStatus.OPEN:
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-card-border text-title">
              {t('status.open', 'Abierto')}
            </span>
          );
        case ReturnedReceptionStatus.POR_UBICAR:
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-bg-warning text-warning border border-warning/20">
              {t('status.to_locate', 'Por ubicar')}
            </span>
          );
        case ReturnedReceptionStatus.CLOSED:
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-bg-success text-success border border-success/20">
              {t('status.closed', 'Cerrado')}
            </span>
          );
        case ReturnedReceptionStatus.PENDING_OPEN:
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-card-border light:bg-bg-warning text-warning border border-warning/20">
              {t('status.pending_open', 'Pendiente')}
            </span>
          );
        default:
          return null;
      }
    },
    [t],
  );

  const breadcrumbs = useMemo(
    () => [
      // Solo mostrar "Rehusados" en desktop
      ...(isMobile ? [] : [{ label: t('breadcrumb.reused', 'Rehusados'), path: '/reused' }]),
      {
        label: (
          <div className="flex items-center gap-2">
            <span>{receptionCode || `${t('breadcrumb.reception', 'Recepción')} #${receptionId}`}</span>
            {getStatusBadge(receptionStatus)}
          </div>
        ),
        title: receptionCode || `${t('breadcrumb.reception', 'Recepción')} #${receptionId}`,
      },
    ],
    [receptionId, receptionCode, receptionStatus, isMobile, getStatusBadge, t],
  );

  const headerActions = useMemo(() => {
    const actions = [];

    // Botón de imprimir etiqueta: visible en status 2 (Por ubicar), 0 (Cerrado) o 1 (Abierto)
    if ((receptionStatus === 2 || receptionStatus === 0 || receptionStatus === 1) && onPrintClick) {
      actions.push({
        id: 'print-reused-label',
        label: isMobile ? '' : t('actions.print_label', 'Imprimir Etiqueta'),
        icon: <Printer className="w-4 h-4" />,
        onClick: onPrintClick,
        variant: 'outline' as const,
      });
    }

    // Botón de finalizar recepción: visible solo si no está en status 2 o 0
    if (receptionStatus !== 2 && receptionStatus !== 0 && onFinalizeClick) {
      actions.push({
        id: 'finalize-reception',
        label: isMobile ? '' : t('actions.finalize', 'Finalizar Recepción'),
        icon: <CheckCircle2 className="w-4 h-4" />,
        onClick: onFinalizeClick,
        variant: 'primary' as const,
        disabled: isFinalizeDisabled,
        tooltip: finalizeTooltip,
      });
    }

    return actions;
  }, [receptionStatus, onFinalizeClick, onPrintClick, isMobile, t, isFinalizeDisabled, finalizeTooltip]);

  usePageNavigation({ breadcrumbs, headerActions });

  return null;
};
